/**
 * HotpointHelper - Helper for detecting and managing hotpoints in player
 *
 * Hotpoints define special time ranges in video content:
 * - 'intro': Opening credits/intro sequence
 * - 'outro': Ending credits/outro sequence
 * - 'titrage': Title sequence that can be skipped
 *
 * Used for timing next content cards and skip buttons.
 * Ported from App-WebTV/src/utils/hotpointHelper.js
 */

/**
 * Get the semantic type from either current CMS shape (`hotpointType`) or
 * legacy shape (`type`).
 * @param {Object} hotpoint - Hotpoint object
 * @returns {string}
 */
export function getHotpointKind(hotpoint) {
    return String(hotpoint?.hotpointType || hotpoint?.type || "").toLowerCase();
}

/**
 * Get the outro hotpoint from hotpoints array
 * @param {Array} hotPoints - Array of hotpoint objects
 * @returns {Object|null} - Outro hotpoint or null if not found
 */
export function getOutroHotpoint(hotPoints) {
    if (!hotPoints || !Array.isArray(hotPoints)) {
        return null;
    }
    var outro = hotPoints.find(function (hotpoint) {
        return getHotpointKind(hotpoint) === "outro";
    });
    return outro || null;
}

/**
 * Get the intro hotpoint from hotpoints array
 * @param {Array} hotPoints - Array of hotpoint objects
 * @returns {Object|null} - Intro hotpoint or null if not found
 */
export function getIntroHotpoint(hotPoints) {
    if (!hotPoints || !Array.isArray(hotPoints)) {
        return null;
    }
    var intro = hotPoints.find(function (hotpoint) {
        var kind = getHotpointKind(hotpoint);
        return kind === "intro" || kind === "titrage";
    });
    return intro || null;
}

/**
 * Check if a hotpoint has valid timing data
 * @param {Object} hotpoint - Hotpoint object
 * @returns {boolean}
 */
export function isValidHotpoint(hotpoint) {
    return (
        hotpoint &&
        hotpoint.startTime != null &&
        hotpoint.endTime != null &&
        hotpoint.startTime > 0 &&
        hotpoint.endTime > hotpoint.startTime
    );
}

/**
 * Check if current position is within a hotpoint's time range
 * @param {number} currentTime - Current playback time in seconds
 * @param {Object} hotpoint - Hotpoint object with startTime and endTime
 * @returns {boolean}
 */
export function isPositionInHotpoint(currentTime, hotpoint) {
    if (!isValidHotpoint(hotpoint)) {
        return false;
    }
    return currentTime >= hotpoint.startTime && currentTime <= hotpoint.endTime;
}

/**
 * Calculate when to show next content card based on outro hotpoint
 * For music: show during outro OR last 15 seconds
 * For episodes: show during outro OR last 45 seconds
 *
 * @param {number} duration - Total video duration in seconds
 * @param {Array} hotPoints - Array of hotpoint objects
 * @param {string} contentType - Content type ('music' or 'series'/'program')
 * @returns {Object} - { triggerTime: number, endTime: number, useHotpoint: boolean }
 */
export function getNextContentTriggerTime(duration, hotPoints, contentType) {
    var outro = getOutroHotpoint(hotPoints);

    if (isValidHotpoint(outro)) {
        return {
            triggerTime: outro.startTime,
            endTime: outro.endTime,
            useHotpoint: true,
        };
    }

    // Fallback timing based on content type
    var fallbackSeconds = contentType === "music" ? 15 : 45;
    var triggerTime = Math.max(0, duration - fallbackSeconds);

    return {
        triggerTime: triggerTime,
        endTime: duration,
        useHotpoint: false,
    };
}

/**
 * Check if we should show next content card at current time
 * @param {number} currentTime - Current playback time in seconds
 * @param {number} duration - Total video duration in seconds
 * @param {Array} hotPoints - Array of hotpoint objects
 * @param {string} contentType - Content type ('music' or 'series'/'program')
 * @returns {boolean}
 */
export function shouldShowNextContent(
    currentTime,
    duration,
    hotPoints,
    contentType
) {
    var timing = getNextContentTriggerTime(duration, hotPoints, contentType);
    return currentTime >= timing.triggerTime && currentTime < timing.endTime;
}

/**
 * Calculate remaining time until content end from current position
 * @param {number} currentTime - Current playback time in seconds
 * @param {number} duration - Total video duration in seconds
 * @returns {number} - Remaining seconds (always >= 0)
 */
export function getRemainingTime(currentTime, duration) {
    var remaining = Math.max(0, duration - currentTime);
    return Math.floor(remaining);
}

const HotpointHelper = {
    getHotpointKind,
    getOutroHotpoint,
    getIntroHotpoint,
    isValidHotpoint,
    isPositionInHotpoint,
    getNextContentTriggerTime,
    shouldShowNextContent,
    getRemainingTime,
};

export default HotpointHelper;
